import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Bitcoin,
  TrendingUp,
  Link2,
  Droplets,
  ArrowUpRight,
  Copy,
  Clock,
  Zap,
  Users,
  Gift,
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [recentEarnings, setRecentEarnings] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, chartRes, historyRes, refRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/earnings/chart`, { withCredentials: true }),
        axios.get(`${API}/earnings/history?limit=10`, { withCredentials: true }),
        axios.get(`${API}/referrals/stats`, { withCredentials: true }),
      ]);

      setStats(statsRes.data);
      setChartData(chartRes.data);
      setRecentEarnings(historyRes.data);
      setReferralStats(refRes.data);
      await refreshUser();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch dashboard data:', error);
      }
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/auth?ref=${user?.referral_code}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for older browsers or non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Referral link copied!');
    } catch (err) {
      toast.error('Could not copy - please copy the link manually');
    }
  };

  const statCards = [
    {
      label: 'Total Balance',
      value: `${(user?.balance_satoshis || 0).toLocaleString()} sats`,
      subValue: `≈ $${((user?.balance_satoshis || 0) * 0.0005).toFixed(2)}`,
      icon: Bitcoin,
      color: 'text-[#F7931A]',
      bg: 'bg-[#F7931A]/10',
      testId: 'user-btc-balance',
    },
    {
      label: 'Total Clicks',
      value: (stats?.total_clicks || 0).toLocaleString(),
      subValue: `${stats?.earnings_today || 0} sats today`,
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10',
      testId: 'user-total-clicks',
    },
    {
      label: 'Active Links',
      value: (user?.total_links || 0).toLocaleString(),
      subValue: 'Earning links',
      icon: Link2,
      color: 'text-cyan-400',
      bg: 'bg-cyan-400/10',
    },
    {
      label: 'Total Earned',
      value: `${(stats?.total_earnings || 0).toLocaleString()} sats`,
      subValue: 'Lifetime earnings',
      icon: Zap,
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
    },
  ];

  const getEarningTypeIcon = (type) => {
    switch (type) {
      case 'click':
        return <Link2 className="w-4 h-4 text-cyan-400" />;
      case 'faucet':
        return <Droplets className="w-4 h-4 text-emerald-400" />;
      case 'referral':
        return <Users className="w-4 h-4 text-purple-400" />;
      default:
        return <Bitcoin className="w-4 h-4 text-[#F7931A]" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6 lg:px-8" data-testid="user-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
            Welcome back, <span className="text-gradient-gold">{user?.username}</span>
          </h1>
          <p className="text-slate-400">Here's your earning overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <div
              key={stat.label}
              className="card-dark p-6 animate-fade-in-up"
              style={{ animationDelay: `${i * 0.1}s` }}
              data-testid={stat.testId}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <ArrowUpRight className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="font-mono text-2xl font-bold mb-1">{stat.value}</div>
              <div className="text-sm text-slate-400">{stat.label}</div>
              <div className="text-xs text-slate-500 mt-1">{stat.subValue}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Link to="/links">
            <div className="card-dark p-6 hover:border-[#F7931A]/50 transition-all cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F7931A]/10 flex items-center justify-center">
                    <Link2 className="w-6 h-6 text-[#F7931A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Create New Link</h3>
                    <p className="text-sm text-slate-400">Shorten a URL and start earning</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-[#F7931A] transition-colors" />
              </div>
            </div>
          </Link>

          <Link to="/faucet">
            <div className="card-dark p-6 hover:border-emerald-500/50 transition-all cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                    <Droplets className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Claim Faucet</h3>
                    <p className="text-sm text-slate-400">Get free satoshis every 15 min</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-400 transition-colors" />
              </div>
            </div>
          </Link>
        </div>

        {/* Charts and History */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Earnings Chart */}
          <div className="lg:col-span-2 card-dark p-6">
            <h3 className="font-heading text-lg font-semibold mb-6">Earnings (Last 7 Days)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1A1E2E" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748B"
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { weekday: 'short' });
                    }}
                  />
                  <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: '#131622',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#94A3B8' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="earnings"
                    stroke="#F7931A"
                    strokeWidth={2}
                    dot={{ fill: '#F7931A', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Earnings */}
          <div className="card-dark p-6">
            <h3 className="font-heading text-lg font-semibold mb-6">Recent Activity</h3>
            <div className="space-y-4">
              {recentEarnings.length > 0 ? (
                recentEarnings.slice(0, 6).map((earning) => (
                  <div
                    key={earning.id}
                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      {getEarningTypeIcon(earning.type)}
                      <div>
                        <p className="text-sm font-medium truncate max-w-[150px]">
                          {earning.description}
                        </p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(earning.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="font-mono text-sm text-emerald-400">
                      +{earning.amount_satoshis}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-8">
                  No earnings yet. Start by creating links or claiming from the faucet!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="mt-8 card-dark p-6" data-testid="dashboard-referral-section">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h3 className="font-heading text-lg font-semibold">Your Referral Program</h3>
                  {referralStats && (
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-mono uppercase tracking-widest ${
                        referralStats.current_tier === 3
                          ? 'bg-gradient-to-r from-[#FFD700] to-[#F7931A] text-black border border-[#FFD700] shadow-[0_0_12px_rgba(255,215,0,0.4)]'
                          : referralStats.current_tier === 2
                          ? 'bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/40'
                          : 'bg-slate-500/10 text-slate-400 border border-slate-500/30'
                      }`}
                      data-testid="referral-tier-badge"
                    >
                      {referralStats.current_tier === 3
                        ? `👑 Elite · ${referralStats.commission_rate}%`
                        : `Tier ${referralStats.current_tier} · ${referralStats.commission_rate}%`}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">
                  Earn <span className="text-emerald-400 font-semibold">{referralStats?.signup_bonus || 100} sats</span> per signup +
                  <span className="text-[#F7931A] font-semibold"> {referralStats?.commission_rate || 10}%</span> of everything they earn — forever
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="text-center">
                <p
                  className="font-mono text-xl font-bold text-purple-400"
                  data-testid="referral-total-count"
                >
                  {referralStats?.total_referrals || 0}
                </p>
                <p className="text-xs text-slate-500">Referrals</p>
              </div>
              <div className="text-center">
                <p
                  className="font-mono text-xl font-bold text-emerald-400"
                  data-testid="referral-earnings-value"
                >
                  {(referralStats?.referral_earnings || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Sats Earned</p>
              </div>
            </div>
          </div>

          {/* Tier progress bar - shown when NOT at highest tier */}
          {referralStats && referralStats.current_tier < 3 && referralStats.next_tier_at && (
            <div className="mt-6 p-4 rounded-lg bg-[#0D0F17] border border-white/5" data-testid="referral-tier-progress-block">
              <div className="flex items-center justify-between text-xs mb-2 flex-wrap gap-2">
                <span className="text-slate-400 flex items-center gap-2">
                  <Zap className="w-3 h-3 text-[#F7931A]" />
                  Unlock{' '}
                  {referralStats.current_tier === 2 ? (
                    <span className="text-[#FFD700] font-semibold">👑 Elite</span>
                  ) : (
                    <span className="text-[#F7931A] font-semibold">Power Referrer</span>
                  )}
                  {' '}(
                  {referralStats.current_tier === 2
                    ? `${referralStats.tier_rates.tier_3}% commission`
                    : `${referralStats.tier_rates.tier_2}% commission`}
                  )
                </span>
                <span className="text-slate-300 font-mono">
                  {referralStats.total_referrals} / {referralStats.next_tier_at}
                </span>
              </div>
              <div className="h-2 bg-[#131622] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    referralStats.current_tier === 2
                      ? 'bg-gradient-to-r from-[#F7931A] via-[#FFD700] to-[#FFD700]'
                      : 'bg-gradient-to-r from-purple-500 to-[#F7931A]'
                  }`}
                  style={{
                    width: `${Math.min(100, (referralStats.total_referrals / referralStats.next_tier_at) * 100)}%`,
                  }}
                  data-testid="referral-tier-progress-bar"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {referralStats.referrals_until_next_tier} more referrals to boost your commission from{' '}
                {referralStats.commission_rate}% to{' '}
                {referralStats.current_tier === 2
                  ? referralStats.tier_rates.tier_3
                  : referralStats.tier_rates.tier_2}
                %
              </p>
            </div>
          )}

          {referralStats?.current_tier === 2 && (
            <div className="mt-6 p-4 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/30" data-testid="referral-power-tier-block">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#F7931A]" />
                <div>
                  <p className="text-sm font-semibold text-[#F7931A]">Power Referrer Unlocked</p>
                  <p className="text-xs text-slate-400">You now earn 15% on every referral's activity</p>
                </div>
              </div>
            </div>
          )}

          {referralStats?.current_tier === 3 && (
            <div
              className="mt-6 p-4 rounded-lg bg-gradient-to-br from-[#FFD700]/15 via-[#F7931A]/10 to-transparent border border-[#FFD700]/40 shadow-[0_0_24px_rgba(255,215,0,0.15)]"
              data-testid="referral-elite-tier-block"
            >
              <div className="flex items-center gap-3">
                <div className="text-2xl leading-none">👑</div>
                <div>
                  <p className="text-sm font-semibold text-[#FFD700]">Elite Referrer Unlocked</p>
                  <p className="text-xs text-slate-400">
                    Legendary status — you now earn 20% on every referral's activity, forever
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-2">
            <div className="flex-1 px-4 py-3 bg-[#0D0F17] rounded-lg border border-white/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Gift className="w-4 h-4 text-[#F7931A] flex-shrink-0" />
                <span
                  className="font-mono text-sm text-white truncate"
                  data-testid="user-referral-link-input"
                >
                  {`${window.location.origin}/auth?ref=${user?.referral_code}`}
                </span>
              </div>
              <span className="font-mono text-xs text-[#F7931A] bg-[#F7931A]/10 px-2 py-1 rounded flex-shrink-0">
                {user?.referral_code}
              </span>
            </div>
            <Button
              onClick={copyReferralLink}
              className="btn-gold px-6"
              data-testid="copy-referral-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
          </div>

          {user?.referral_code && (
            <div className="mt-6">
              <ShareButtons
                referralUrl={`${window.location.origin}/auth?ref=${user.referral_code}`}
                referralCode={user.referral_code}
              />
            </div>
          )}

          {referralStats?.referred_users?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
                Recent Referrals
              </p>
              <div className="flex flex-wrap gap-2">
                {referralStats.referred_users.slice(0, 8).map((ref) => (
                  <div
                    key={ref.username}
                    className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-sm text-purple-300"
                  >
                    @{ref.username}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
